// @ts-strict-ignore
import { Component, OnInit } from '@angular/core';
import { WagoApiStatusCode } from '@shared/service/wago-api-status-code';
import { ValidatorFactory } from '@shared/validator.factory';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ProjectModel } from '@project/model/project.model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserVO } from '@user/model/vo/user.vo';
import { VolatileDataSetHolder } from '@project/util/volatile-data-set.holder';
import { UserUtils } from '@user/service/user-utils';
import { ArticleDataService } from '@article/service/article-data.service';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { CustomValidators } from '@shared/custom-validators';
import { ProjectService } from '@project/components/import-export/service/project.service';
import { ProjectSaveInfoVO } from '@project/model/vo/project.vo';
import { PriceCalculationRequestProjectVO, PriceCalculationRequestVO, PriceCalculationResultVO } from '@project/model/vo/price-calculation-data.vo';
import { StringUtils } from '@shared/util/string-utils';
import { LabelInfo, PriceCalculationHelper } from '@project/components/request/project-request-price-calculation/price-calculation.helper';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { escape } from 'lodash';
import { SystemConfigurationService } from '@shared/system-configuration/system-configuration-loading.service';
import { ProjectDataService } from '../../../service/project-data.service';
import { IsProjectType } from '../../../model/is-project-type';
import { lastValueFrom } from 'rxjs';
import { ProjectComponentPresenter } from '../../project-component.presenter';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  standalone: false,
  templateUrl: './project-request-price-calculation.component.html',
  selector: 'app-project-request-price-calculation',
})
export class ProjectRequestPriceCalculationComponent implements OnInit {
  public form: FormGroup;
  public showRequiredFields = false;
  public startDate: Date;
  public endDate: Date;
  public highestFinalizedProjectVersion: number;
  public highestFinalizedProjectVersionDisplayStringWithDate: string;
  public bsValue: any;

  public useCustomAddressNumber: boolean = false;
  private userAddressNumber: string;
  private userSalesOrganisation: string;

  constructor(
    private formBuilder: FormBuilder,
    private bsModalRef: BsModalRef,
    private projectModel: ProjectModel,
    private projectService: ProjectService,
    private projectDataService: ProjectDataService,
    private localeResourceManager: LocaleResourceManager,
    private articleDataService: ArticleDataService,
    private alertHelper: AlertHelper,
    private priceCalculationHelper: PriceCalculationHelper,
    private systemConfigurationService: SystemConfigurationService,
    private projectComponentPresenter: ProjectComponentPresenter,
    private validatorFactory: ValidatorFactory,
  ) {}

  public ngOnInit(): void {
    this.projectDataService.loadProjectSaveInfo(this.projectModel.currentProject.id).subscribe((projectSaveInfo: ProjectSaveInfoVO) => {
      if (projectSaveInfo) {
        this.highestFinalizedProjectVersion = projectSaveInfo.highestFinalizedProjectVersion;
        this.highestFinalizedProjectVersionDisplayStringWithDate = ProjectUtils.getHighestFinalizedVersionDisplayStringFromProjectSaveInfo(
          projectSaveInfo,
          true
        );
      }
    });
    if (UserUtils.isWagoUser(this.projectModel.user)) {
      this.useCustomAddressNumber = true;
      this.userAddressNumber = VolatileDataSetHolder.instance.getDataSetValue(VolatileDataSetHolder.DATA_SET_PROJECT_PRICING, VolatileDataSetHolder.DATA_SET_CUSTOM_ADDRESS_NUMBER_KEY);
      this.userSalesOrganisation = VolatileDataSetHolder.instance.getDataSetValue(VolatileDataSetHolder.DATA_SET_PROJECT_PRICING, VolatileDataSetHolder.DATA_SET_CUSTOM_SALES_ORGANISATION_KEY);
    }
    this.startDate = new Date();
    this.startDate.setDate(this.startDate.getDate() + this.systemConfigurationService.currentConfiguration.requestDaysInFuture);
    this.endDate = new Date();
    this.endDate.setFullYear(this.endDate.getFullYear() + 1);
    this.form = this.formBuilder.group(this.getFormModel());
    this.form.controls['totalDemand'].setValue('1');
    this.form.controls['batchSize'].setValue('1');
    this.form.controls['deliveryDate'].setValue(this.startDate);
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  public getHeader(): string {
    return this.localeResourceManager.getValueByKey('projectRequestPopup.header', [this.projectModel.currentProject.name]);
  }

  public checkControlValidity(controlName: string): boolean {
    const control = this.form.controls[controlName];
    return (control.invalid && control.touched) || (control.invalid && this.showRequiredFields);
  }

  public requestPrice(): void {
    if (!this.form.valid) {
      this.showRequiredFields = true;
      return;
    }
    this.sendRequestPrice();
  }

  public sendRequestPrice(): void {
    const projectVO: PriceCalculationRequestProjectVO = new PriceCalculationRequestProjectVO();
    projectVO.projectId = this.projectModel.currentProject.id;
    projectVO.projectVersion = this.projectModel.currentProject.currentVersion;

    projectVO.projectWidth = this.priceCalculationHelper.getProjectWidth();

    const info: LabelInfo = this.priceCalculationHelper.findLabelCount();
    projectVO.labelCount = info.labelCount;
    projectVO.notDefaultLabelCount = info.notDefaultLabelCount;
    projectVO.directPrintingData = info.directPrintingData;

    if (IsProjectType.mcsGds(this.projectModel.currentProjectType)) {
      projectVO.brokenCodingFinger = this.priceCalculationHelper.findBrokenCodingFingerCount();
    }

    const user: UserVO = this.projectModel.user;
    if (this.useCustomAddressNumber) {
      this.userAddressNumber = this.form.value.addressNumber;
      // 26515: leading zeros need to be stripped from user address numbers:
      this.userAddressNumber = parseInt(this.form.value.addressNumber).toString();

      this.userSalesOrganisation = this.form.value.salesOrganisation;
      VolatileDataSetHolder.instance.setDataSetValue(
        VolatileDataSetHolder.DATA_SET_PROJECT_PRICING,
        VolatileDataSetHolder.DATA_SET_CUSTOM_ADDRESS_NUMBER_KEY,
        this.userAddressNumber
      );
      VolatileDataSetHolder.instance.setDataSetValue(
        VolatileDataSetHolder.DATA_SET_PROJECT_PRICING,
        VolatileDataSetHolder.DATA_SET_CUSTOM_SALES_ORGANISATION_KEY,
        this.userSalesOrganisation
      );
    } else {
      this.userAddressNumber = user.addressNumber;
      this.userSalesOrganisation = user.salesOrganisation;
    }
    const requestVO: PriceCalculationRequestVO = new PriceCalculationRequestVO();
    requestVO.userId = user.id;
    requestVO.locale = this.localeResourceManager.currentLanguage.langKey;
    requestVO.receiverMail = user.email;
    requestVO.totalDemand = this.form.value.totalDemand;
    requestVO.batchSize = this.form.value.batchSize;
    requestVO.deliveryDate = this.form.value.deliveryDate;
    requestVO.remarks = this.form.value.remarks;
    requestVO.isCustomAddressNumber = this.useCustomAddressNumber;
    requestVO.userAddressNumber = this.userAddressNumber;
    requestVO.userSalesOrganisation = this.userSalesOrganisation;
    requestVO.sapProjectNumber = this.form.value.sapProjectNumber;

    lastValueFrom(this.articleDataService.calculateProjectPrice(requestVO, projectVO))
      .then((response) => this.handlePriceRequestSuccess(response))
      .catch((error) => this.handlePriceRequestError(error));
  }


  private handlePriceRequestSuccess(response: PriceCalculationResultVO) {
    if (response.error) {
      const text = StringUtils.join(response.error, (e) => e.message, '\n');
      this.alertHelper
        .showAlert(
          'projectRequestPopup.title.error_calculating_price',
          'projectRequestPopup.message.failure_calculating_price',
          {0: escape(text)},
          [AlertButton.YES, AlertButton.CANCEL],
          "error"
        )
        .then((answer) => {
          if (answer === AlertButton.YES) {
            this.close();
            this.projectComponentPresenter.openProjectRequestDialog(
              null,
              this.form.value.totalDemand,
              this.form.value.batchSize,
              this.form.value.deliveryDate,
              this.form.value.remarks
            );
          }
        });
    } else {
      this.projectService.updateProjectDataLoaded(response.project);
      this.alertHelper.showAlert('', 'projectRequestPopup.price_calculation.success', null, [AlertButton.OK]);
      this.close();
    }
  }

  private handlePriceRequestError(error: HttpErrorResponse) {
    if (error.status === WagoApiStatusCode.ServiceUnavailable) {
      this.alertHelper.showError('serverCommunication.title.service_not_active', 'serverCommunication.message.service_not_active');
    } else {
      this.alertHelper
        .showAlert(
          'projectRequestPopup.title.error_calculating_price',
          'projectRequestPopup.message.general_error_calculating_price',
          null,
          [AlertButton.YES, AlertButton.CANCEL],
          "exclamation"
        )
        .then((answer) => {
          if (answer === AlertButton.YES) {
            this.close();
            this.projectComponentPresenter.openProjectRequestDialog(
              null,
              this.form.value.totalDemand,
              this.form.value.batchSize,
              this.form.value.deliveryDate,
              this.form.value.remarks,
              this.form.value.sapProjectNumber
            );
          }
        });
    }
  }

  private getFormModel(): any {
    return {
      totalDemand: [
        '',
        Validators.compose([
          CustomValidators.trimRequired,
          this.validatorFactory.number({min: 1, max: 99999999, precision: 0}),
        ]),
      ],
      batchSize: [
        '',
        Validators.compose([
          CustomValidators.trimRequired,
          this.validatorFactory.number({min: 1, max: 99999999, precision: 0}),
        ]),
      ],
      deliveryDate: ['', Validators.compose([CustomValidators.required])],
      remarks: [
        '',
        Validators.compose([
          CustomValidators.minMaxLength(0, 1000, '', this.localeResourceManager.getValueByKey('projectRequestPopup.error_tooLongError')),
        ]),
      ],
      addressNumber: this.useCustomAddressNumber
        ? [
            '',
            Validators.compose([
              CustomValidators.trimRequired,
              this.validatorFactory.number({max: 9999999999, precision: 0}),
            ]),
          ]
        : null,
      salesOrganisation: this.useCustomAddressNumber
        ? [
            '',
            Validators.compose([
              CustomValidators.trimRequired,
              this.validatorFactory.number({max: 9999, precision: 0}),
            ]),
          ]
        : null,
      sapProjectNumber: [
        '',
        Validators.compose([
          this.validatorFactory.number({max: 9999999999, precision: 0}),
        ]),
      ],
      acceptTerms: null,
    };
  }
}
